import { animate, state, style, transition, trigger } from '@angular/animations';
import { OverlayModule, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { NgClass, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  effect,
  inject,
  input,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatRippleModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { RouterLinkDetectorDirective } from 'app/core/directives/router-link-detector.directive';
import { Footer } from 'app/core/models/footer.model';
import { provideGtmEvent } from 'app/core/services/google-tag-manager/gtm.directive';
import { useCurrentTemplate } from 'app/core/services/google-tag-manager/use-current-template';
import { SharedService } from 'app/core/services/shared.service';
import { hlm } from 'app/core/utils/hlm';
import { ShebaButtonComponent } from 'app/shared/components/button/button.component';
import { ClassValue } from 'clsx';
import { HamburgerIcon } from '../../../../assets/icons/hamburger/hamburger';
import { HomeIcon } from '../../../../assets/icons/home/home';
import { ProfileIcon } from '../../../../assets/icons/profile/profile';
import { ShebaSearchHeaderComponent } from '../../../shared/components/search-header/search-header.component';
import { ScrollService } from '../../../shared/services/scroll.service';
import { Header } from '../../models/header.model';
import { CmsMediaPrefixPipe } from '../../pipes/cms-media-prefix.pipe';
import { ShebaLangSelectDropdownComponent } from '../sheba-lang-select-dropdown/sheba-lang-select-dropdown.component';
import { ShebaSidenavComponent } from '../sidenav/sidenav.component';
import { ShebaHeadernavComponent } from './headernav/headernav.component';
import { SanitizeUrlPipe } from 'app/core/pipes/sanitize-url.pipe';

const styles = {
  header: [
    'top-0 z-10 mx-auto flex w-full place-content-center place-items-center px-3 py-2.5 transition-colors md:px-8',
    // light theme
    '[&.open]:bg-white bg-[#F2F5F9] [&.scrolled]:bg-white',
    // dark theme
    'dark:text-white  dark:md:bg-[#002024] dark:[&.open]:md:bg-[#002024]  dark:[&.scrolled]:md:bg-[#002024]',
    // dark mobile theme
    'dark:[&.scrolled]:max-md:bg-transparent dark:max-md:bg-transparent dark:max-md:bg-gradient-to-b from-[#002024] to-[#012C31]/60'
  ]
} as const;

@Component({
  selector: 'sheba-header',
  standalone: true,
  host: {
    class: 'contents'
  },
  animations: [
    trigger('openClose', [
      state(
        '*',
        style({
          opacity: 1,
          height: '*'
        })
      ),
      state(
        'void',
        style({
          opacity: 0.0,
          transform: 'translateY(-10%)'
        })
      ),
      transition('* => void', [animate('100ms')]),
      transition('void => *', [animate('150ms')])
    ])
  ],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    ProfileIcon,
    HomeIcon,
    HamburgerIcon,
    MatRippleModule,
    MatTooltipModule,
    OverlayModule,
    ShebaSidenavComponent,
    CmsMediaPrefixPipe,
    RouterLinkDetectorDirective,
    ShebaHeadernavComponent,
    ShebaLangSelectDropdownComponent,
    TranslateModule,
    ShebaButtonComponent,
    ShebaSearchHeaderComponent,
    NgOptimizedImage,
    SanitizeUrlPipe
  ],
  providers: [provideGtmEvent({ eventInfo: { template: useCurrentTemplate() } })]
})
export class ShebaHeaderComponent {
  isOpen = signal(false);
  headerData = input<Header>({} as Header);
  //required for mobile
  footerData = input<Footer>({} as Footer);
  displayMenu = input<boolean>(true);
  displayHamburger = input<boolean>(true);

  activeMenuIndex = signal(0);
  activeIndex = signal(0);

  fixed = input(false, { transform: booleanAttribute });

  readonly userClass = input<ClassValue>('', { alias: 'class' });
  readonly computedHeaderClass = computed(() => hlm(...styles.header, this.userClass()));

  blockScrollStrategy = inject(ScrollStrategyOptions).block();
  scrollService = inject(ScrollService);
  scrollPosition = this.scrollService.scrollPosition;
  lightMode = inject(SharedService).lightMode;
  router = inject(Router);

  currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  isHomePage = computed(() => {
    const url = this.currentUrl();
    return url === '/' ||
           url === '/home' ||
           /^\/(he|en|ar|ru)$/.test(url) ||
           /^\/(he|en|ar|ru)\/home$/.test(url);
  });

  constructor() {
    effect(() => {
      if (this.isOpen()) {
        this.blockScrollStrategy.enable();
      } else {
        this.blockScrollStrategy.disable();
      }
    });
  }
}
